/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, Spinner } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiGetCandidateProfile } from '@/api/services';
import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { Candidate } from '@/types/Candidate';

import CreateUpdateAgentIndex from './CreateUpdateAgentIndex';

export default function CreateScreeningWithCandidatePage() {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const { initializeResumeScreening, reset } = useCreateInterviewAgent();

  // Fetch and initialize screening data
  useEffect(() => {
    fetchCandidate();

    // Cleanup on unmount
    return () => {
      reset();
    };
  }, [candidateId]);

  const handleUpdateSuccess = useCallback(() => {
    // Navigate back to interview list after successful update
    navigate('/interview');
  }, [navigate]);

  const fetchCandidate = useCallback(async () => {
    if (!candidateId) {
      navigate('/interview');
      return;
    }

    setIsLoading(true);
    setFetchError(null);
    try {
      const response = await fetchAPI<Candidate>({
        request: () => apiGetCandidateProfile({ candidateId })
      });

      if (response.success) {
        initializeResumeScreening(response.data);
      } else {
        console.error('Failed to fetch candidate details:', response.message);
        setFetchError('Failed to load candidate data. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching candidate details:', error);
      setFetchError('Failed to load candidate data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" minH="60vh" w="100%">
        <Spinner size="lg" color="primary.900" />
      </Flex>
    );
  }

  if (fetchError) {
    return (
      <Flex justify="center" w="100%" align="center" minH="60vh" direction="column" gap="16px">
        <Box color="error.700" fontSize="16px" textAlign="center" role="alert">
          {fetchError}
        </Box>
        <Button
          variant="outline"
          size="lg"
          borderColor="primary.900"
          color="primary.900"
          borderRadius="8px"
          _hover={{ bg: 'neutrals.50' }}
          onClick={fetchCandidate}
        >
          Try Again
        </Button>
      </Flex>
    );
  }

  return <CreateUpdateAgentIndex onUpdateSuccess={handleUpdateSuccess} />;
}
